import React from 'react'
import { Box } from 'components/layout/Box/Box'
import { BellCurve } from 'components/shared/BellCurve/BellCurve'
import { DoubleEndedScale } from 'components/shared/DoubleEndedScale/DoubleEndedScale'
import styles from './Trait.module.scss'
import { useTrait } from './hooks'
import { Accordion } from 'components/shared/Accordion/Accordion'
import cx from 'classnames'
import { interweave } from 'helpers'
import { CandidateSelect } from 'components/modals/TestsReportModal/common/CandidateSelect/CandidateSelect'
import { CautionSign } from 'components/shared/CautionSign/CautionSign'
import { hasCaution } from '../cautions'
import { t } from 'ttag'

export const Trait = ({
  factor,
  language,
  scores,
  groups,
  name,
  single,
  selectedCandidates,
  setSelectedCandidates,
  response,
  candidates,
  cautions = [],
}) => {
  const { state } = useTrait({
    factor,
    language,
    scores: scores[0],
    response,
    groups,
  })

  const getScale = (data, index) => {
    return (
      <DoubleEndedScale
        {...data}
        expandable
        single={single}
        selectedCandidates={selectedCandidates}
        key={data.label}
        isOpen={state.selectedAttributes[data.id]}
        setIsOpen={state.selectAttribute(data.id)}
        initialPlacement={
          state.attributes.length === index + 1 ? 'left-end' : 'left-start'
        }
      />
    )
  }

  const getAccordeon = ({ label, traits, explanation }, index) => (
    <Accordion
      key={label}
      className={styles.accordeon}
      isInitiallyOpen={state.selectedGroups[label]}
    >
      <Accordion.Header
        title={
          <span className={styles.accordeonHeader}>
            {label}
            <span className={styles.accordeonExplanation}>
              {` - ${explanation}`}
            </span>
          </span>
        }
        onToggle={state.selectGroup(label)}
      />
      <Accordion.Panel className={styles.accordeonPanel}>
        <div className={styles.panelContent}>
          {state.attributes
            .filter(({ id }) => traits.includes(id))
            .map(getScale)}
        </div>
      </Accordion.Panel>
    </Accordion>
  )

  const disabled = state.allScores.reduce(
    (acc, trait) => ({ ...acc, [trait.id]: isNaN(trait.score) }),
    {},
  )

  return (
    <Box className={styles.trait}>
      <div className={styles.leftSection}>
        <div className={styles.title}>
          <div className={styles.titleText}>
            {state.label[0]}
            {hasCaution(cautions, factor) ? (
              <CautionSign placement="bottom" />
            ) : null}
          </div>
          {single ? <div className={styles.name}>{name}</div> : null}
        </div>
        <div className={styles.select}>
          {single ? null : (
            <CandidateSelect
              {...{
                candidates,
                selectedCandidates,
                setSelectedCandidates,
                disabled,
              }}
            />
          )}
        </div>
        <BellCurve
          allScores={state.allScores}
          candidates={candidates}
          selectedCandidates={selectedCandidates}
          className={styles.bellCurve}
          single={single}
        />
        <br />
        {state.abstract.map((par, index) => (
          <div key={index} className={styles.paragraph}>
            {par}
          </div>
        ))}
        {single ? (
          <div className={cx(styles.paragraph, styles.description)}>
            {interweave(state.description, ' ')}
          </div>
        ) : null}
      </div>
      <div className={styles.rightSection}>
        <div
          className={cx(styles.expandAll, {
            [styles.withAccordeon]: !!groups,
          })}
          onClick={state.selectAllAttributes}
        >
          {state.allExpanded ? t`collapse all` : t`expand all`}
        </div>
        {groups
          ? React.Children.toArray(
              interweave(
                groups.map(getAccordeon),
                <div className={styles.divider}></div>,
              ),
            )
          : state.attributes.map(getScale)}
      </div>
    </Box>
  )
}
